import type { ScorecardSection as ScorecardData, SectionValue } from "../../../../shared/analysis-types";
import { ScorecardSection } from "./scorecard-section";
import { SectionErrorFallback } from "@/components/narrative/section-error-fallback";
import { useAnalysisCatalog } from "./use-analysis-catalog";

interface CounselingSkillsScorecardProps {
  data: SectionValue<ScorecardData>;
  promptVersion: string;
  className?: string;
}

export function CounselingSkillsScorecard({
  data,
  promptVersion,
  className,
}: CounselingSkillsScorecardProps) {
  const { catalog } = useAnalysisCatalog(promptVersion);

  if ("error" in data) {
    return (
      <div className={className}>
        <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
          Counseling Skills
        </h3>
        <div className="mt-4">
          <SectionErrorFallback sectionName="counseling_skills_scorecard" />
        </div>
      </div>
    );
  }

  // Gate on the catalog so labels resolve before first paint, matching the
  // legacy synchronous registry (no raw-id label flash).
  if (!catalog) return null;

  const registryMap = new Map(catalog.counselingSkills.map((def) => [def.id, def]));

  const resolved = data.items
    .filter((item) => item.rating !== "na")
    .map((item) => ({
      id: item.id,
      rating: item.rating as Exclude<typeof item.rating, "na">,
      label: registryMap.get(item.id)?.label ?? item.id,
      summary: item.summary,
      evidence: item.evidence,
      what_to_try: item.what_to_try,
      why_it_matters: item.why_it_matters,
      copy_paste_reinforcement: item.copy_paste_reinforcement,
    }));

  return (
    <ScorecardSection
      title="Counseling Skills"
      items={resolved}
      className={className}
    />
  );
}
